import React, { useState, useEffect } from "react";
import { fontColorBlue, fontColorDark, calloutField2 } from "./shared.module.scss";
import FloorplansDisplay from "./floorplansDisplay";
import { getYardiPropId, getYardiPropIdFromSlug } from "../utils/propertyMapUtil";
import { safeFetch, safeParseUnitsData, safeParseDealsData, createSectionErrorLogger } from "../utils/apiUtils";
import { transformDeals, transformUnits, getFloorplanNameYardi } from "../utils/floorplanTransforms";

const Floorplans = ({
  slug,
  cid,
  flrplns,
  area,
  citySlug,
  utilitiesIncluded = false,
  cname = '',
  floorplanCalloutField = '',
  email = null,
  crossSellingFloorplans = []
}) => {
  //floorplans are passed in from page
  if (!flrplns) {
    flrplns = [];
  }
  //console.log('flrplns',flrplns);

  const [floorPlans, setFloorPlans] = useState(flrplns);
  const [areFloorPlansSorted, setAreFloorPlansSorted] = useState(false);
  const [loadingFloorPlans, setLoadingFloorPlans] = useState(true);
  const [available, setAvailable] = useState([]);
  const [viewing, setViewing] = useState([]);
  const [startingPricesAv, setStartingPricesAv] = useState({});
  const [endingPricesAv, setEndingPricesAv] = useState({});
  const [startingPrices, setStartingPrices] = useState({});
  const [endingPrices, setEndingPrices] = useState({});
  const [allPrices, setAllPrices] = useState({});
  const [deals, setDeals] = useState({});
  const [floorplanDeals, setFloorplanDeals] = useState([]); //a list of all floor plans that have a deal
  const [soldOutBedCategories, setSoldOutBedCategories] = useState([]); // Track which bed categories are sold out
  const [crossSellPrices, setCrossSellPrices] = useState({}); // Store prices for cross-sell floorplans
  const [filteredCrossSell, setFilteredCrossSell] = useState([]); // Track filtered cross-sell floorplans

  // Function to detect which bedroom categories are sold out
  const detectSoldOutCategories = (units, floorplans) => {
    // Immediately rentable statuses (for cross-sell trigger logic)
    // "Notice Unrented" is excluded because those units are still available (just "Available Soon")
    const immediatelyRentableStatuses = [
      "Vacant Unrented Ready",
      "Vacant Unrented Not Ready"
    ];

    // Group floorplans by bedroom count
    const bedCategories = {};
    floorplans.forEach(fp => {
      const beds = fp.node.beds;
      if (!bedCategories[beds]) {
        bedCategories[beds] = [];
      }
      bedCategories[beds].push(fp.node.floorplanName);
    });

    // Check each bedroom category for availability
    const soldOutCategories = [];
    Object.keys(bedCategories).forEach(bedCount => {
      const floorplanNames = bedCategories[bedCount];
      
      // Check if any units for these floorplans are immediately rentable
      let hasImmediatelyRentableUnit = false;
      for (const unit of units) {
        const fpName = getFloorplanNameYardi(unit.amenities).trim();
        if (floorplanNames.includes(fpName) && immediatelyRentableStatuses.includes(unit.status)) {
          hasImmediatelyRentableUnit = true;
          break;
        }
      }

      // If no immediately rentable units found for this bedroom category, trigger cross-sell
      if (!hasImmediatelyRentableUnit) {
        soldOutCategories.push(parseInt(bedCount));
      }
    });

    return soldOutCategories;
  };

  useEffect(() => {
    if (flrplns && flrplns.length > 0 && available && available.length > 0) {
      const sortedFloorPlans = [...flrplns].sort((a, b) => {
        return a.node.sqFt - b.node.sqFt;
      });
      setFloorPlans(sortedFloorPlans);
    }
  }, [flrplns, available]);

  useEffect(() => {
    // wait for floorplan deals
    if (floorplanDeals.length > 0) {
      setLoadingFloorPlans(false);
    }
  }, [floorplanDeals]);

  useEffect(() => {
    const yardi_cid = getYardiPropId(cid);
    
    // Create error logger for this section
    const logError = createSectionErrorLogger('community_floorplans', {
      yardi_cid: yardi_cid,
      community_slug: slug,
      cid: cid
    });
    
    // Context for all API calls
    const apiContext = {
      section: 'community_floorplans',
      yardi_cid: yardi_cid,
      community_slug: slug,
      cid: cid
    };

    // Load all data with individual error handling
    const loadAllData = async () => {
      try {
        // 1) Fetch deals first
        const dealsData = await safeFetch(
          "https://redisut.mcklab.com/index.php?action=getDeals",
          "deals",
          apiContext
        );
        
        let result = [];
        if (dealsData) {
          result = safeParseDealsData(dealsData, 'deals', apiContext);
        }
        
        if (result && Array.isArray(result)) {
          // Apply transformations using the new utility
          const transformedDeals = transformDeals(result, cid);
          const cd = transformedDeals.map(r => r.sdesc);
          setFloorplanDeals(cd);
          setDeals(transformedDeals);
        } else {
          setFloorplanDeals([]);
          setDeals({});
        }

        // 2) Fetch units data - get data from api for available units
        let yardi_cid = getYardiPropId(cid);
        let unitsResult = [];

        // Fetch main units - critical
        const mainUnitsData = await safeFetch(
          `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${yardi_cid}`,
          "main units",
          apiContext
        );
        if (mainUnitsData) {
          const parsedData = safeParseUnitsData(mainUnitsData, 'main', apiContext);
          unitsResult = unitsResult.concat(parsedData);
        }

        // Additional fetches for multi-building communities - critical for complete data
        const additionalFetches = [];

        if (yardi_cid == "beachfl1") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl2",
              "beachfl2 units",
              apiContext
            ),
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl3",
              "beachfl3 units",
              apiContext
            )
          );
        } else if (yardi_cid == "willfla1") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla2",
              "willfla2 units",
              apiContext
            )
          );
        } else if (yardi_cid == "golfla12") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla03",
              "golfla03 units",
              apiContext
            )
          );
        } else if (yardi_cid == "sc1") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc2",
              "sc2 units",
              apiContext
            ),
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc3",
              "sc3 units",
              apiContext
            )
          );
        } else if (yardi_cid == "westfla1") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=westfla2",
              "westfla2 units",
              apiContext
            )
          );
        } else if (yardi_cid == "mcdonald") {
          additionalFetches.push(
            safeFetch(
              "https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=gleno2",
              "gleno2 units",
              apiContext
            )
          );
        }

        // Wait for all additional fetches
        const additionalUnitsData = await Promise.all(additionalFetches);

        // Process additional units data
        additionalUnitsData.forEach((data, index) => {
          if (data) {
            const parsedData = safeParseUnitsData(data, `additional_${index}`, apiContext);
            unitsResult = unitsResult.concat(parsedData);
          }
        });

        // Apply community-specific transformations using the new utility
        const a = transformUnits(unitsResult, yardi_cid);

        // Set the processed data
        setAvailable(a);
        
      } catch (error) {
        logError(error, 'load_all_data');
        
        // Set empty defaults on error
        setAvailable([]);
        setDeals({});
        setFloorplanDeals([]);
      }
    };

    // Load all data
    loadAllData();

    restoreViewing();
  }, []);

  useEffect(() => {
    const notav = [
      "Occupied No Notice",
      "Notice Rented",
      "Vacant Rented Ready",
      "Vacant Rented Not Ready",
      "Down",
    ]; // Not available status

    const calculatePrices = (data, isLowest) => {
      //console.log('data fish',data);
      const prices = {};
      const pricesAv = {};
      data.forEach((n) => {
        const baseRent = Number(n.rent1);
        //const baseRent = Number(n.rent2);
        //const baseRent = Number(n.rent1) > Number(n.rent2) ? Number(n.rent1) : Number(n.rent2);
        const fpname = getFloorplanNameYardi(n.amenities);
        const av = !notav.includes(n.status);

        const updatePrices = (priceObject, condition) => {
          if (fpname in priceObject) {
            if (condition) {
              priceObject[fpname] = baseRent;
            }
          } else {
            priceObject[fpname] = baseRent;
          }
        };

        // Update prices for all units
        updatePrices(
          prices,
          isLowest
            ? Number(prices[fpname]) > baseRent
            : Number(prices[fpname]) < baseRent,
        );

        // Update prices only for available units
        if (av) {
          updatePrices(
            pricesAv,
            isLowest
              ? Number(pricesAv[fpname]) > baseRent
              : Number(pricesAv[fpname]) < baseRent,
          );
        }
      });
      return { prices, pricesAv };
    };
    //console.log('available',available);
    if (available && available.length > 0) {
      const { prices: hp, pricesAv: hpav } = calculatePrices(available, false); // For highest prices
      const { prices: sp, pricesAv: spav } = calculatePrices(available, true); // For starting prices
      //console.log('starting prices', sp);
      //console.log('ending prices', hp);
      //console.log('starting prices only available', spav);
      //console.log('ending prices only available', hpav);

      setEndingPrices(hp); // everything
      setEndingPricesAv(hpav); // only available
      setStartingPrices(sp);
      setStartingPricesAv(spav);
      setAllPrices(sp); // Set allPrices to use starting prices for filtering

      // Detect sold-out bedroom categories
      const soldOutCategories = detectSoldOutCategories(available, flrplns);
      setSoldOutBedCategories(soldOutCategories);
    }
  }, [available]);

  // Fetch prices for cross-sell floorplans and match with deals
  useEffect(() => {
    const fetchCrossSellPrices = async () => {
      if (!crossSellingFloorplans || crossSellingFloorplans.length === 0) {
        return;
      }


      // Group cross-sell floorplans by community
      const communitiesMap = {};
      crossSellingFloorplans.forEach(fp => {
        const community = Array.isArray(fp.community) ? fp.community[0] : fp.community;
        if (community && community.rmpropid) {
          const propId = community.rmpropid;
          if (!communitiesMap[propId]) {
            communitiesMap[propId] = {
              community: community,
              floorplans: []
            };
          }
          communitiesMap[propId].floorplans.push(fp);
        }
      });


      const allPrices = {};

      // Fetch unit data for each cross-sell community
      for (const [propId, data] of Object.entries(communitiesMap)) {
        let yardi_cid = getYardiPropId(propId);
        
        // If yardi_cid is undefined, try getting it from slug
        if (!yardi_cid && data.community.slug) {
          yardi_cid = getYardiPropIdFromSlug(data.community.slug);
        }
        
        if (!yardi_cid) {
          console.error(`Debug - No Yardi CID found for propId ${propId}`);
          continue;
        }
        
        // Context for API call
        const apiContext = {
          section: 'cross_sell_pricing',
          yardi_cid: yardi_cid,
          community_slug: data.community.slug,
          propId: propId
        };
        
        try {
          // Prepare fetches array - main units plus any additional properties
          const fetches = [
            safeFetch(
              `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=${yardi_cid}`,
              `cross-sell units for ${yardi_cid}`,
              apiContext
            )
          ];
          
          // Add additional fetches for properties that combine multiple buildings
          // Schooner Cove combines SC1, SC2, and SC3
          if (yardi_cid === "sc1") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc2`,
                `cross-sell sc2 units`,
                apiContext
              ),
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=sc3`,
                `cross-sell sc3 units`,
                apiContext
              )
            );
          }
          // Beach Flats combines beachfl1, beachfl2, and beachfl3
          else if (yardi_cid === "beachfl1") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl2`,
                `cross-sell beachfl2 units`,
                apiContext
              ),
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=beachfl3`,
                `cross-sell beachfl3 units`,
                apiContext
              )
            );
          }
          // Willow Flats combines willfla1 and willfla2
          else if (yardi_cid === "willfla1") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=willfla2`,
                `cross-sell willfla2 units`,
                apiContext
              )
            );
          }
          // Golf Links combines golfla12 and golfla03
          else if (yardi_cid === "golfla12") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=golfla03`,
                `cross-sell golfla03 units`,
                apiContext
              )
            );
          }
          // McDonald/Glenwood combines mcdonald and gleno2
          else if (yardi_cid === "mcdonald") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=gleno2`,
                `cross-sell gleno2 units`,
                apiContext
              )
            );
          }
          // Westgate Flats combines westfla1 and westfla2
          else if (yardi_cid === "westfla1") {
            fetches.push(
              safeFetch(
                `https://redisut.mcklab.com/index.php?action=getAllUnitsYardiCommunity&cid=westfla2`,
                `cross-sell westfla2 units`,
                apiContext
              )
            );
          }
          
          // Fetch all units data
          const allUnitsData = await Promise.all(fetches);
          
          // Combine all units data
          let parsedData = [];
          allUnitsData.forEach((unitsData, index) => {
            if (unitsData) {
              const parsed = safeParseUnitsData(unitsData, `cross-sell-${index}`, apiContext);
              if (parsed && Array.isArray(parsed)) {
                parsedData = parsedData.concat(parsed);
              }
            }
          });
          
          if (parsedData.length > 0) {
            // Apply transformations to get correct floorplan names
            // This ensures the amenities field has the full floorplan descriptor
            parsedData = transformUnits(parsedData, yardi_cid);
            
            // Calculate prices for each floorplan
            data.floorplans.forEach(fp => {
                
                const floorplanUnits = parsedData.filter(unit => {
                  const fpName = getFloorplanNameYardi(unit.amenities).trim();
                  const matches = fpName === fp.floorplanName.trim();
                  if (matches) {
                  }
                  return matches;
                });


                if (floorplanUnits.length > 0) {
                  const availableStatuses = [
                    "Vacant Unrented Ready",
                    "Vacant Unrented Not Ready", 
                    "Notice Unrented"
                  ];
                  
                  // Get available units only
                  const availableUnits = floorplanUnits.filter(unit => 
                    availableStatuses.includes(unit.status)
                  );
                  
                  
                  const units = availableUnits.length > 0 ? availableUnits : floorplanUnits;
                  
                  // Get both market and proforma prices
                  // Market = current actual rent (rent1 from API)
                  // Proforma = projected/list price (rent2 from API)
                  const marketPrices = units.map(u => Number(u.rent1)).filter(p => p > 0);
                  const proformaPrices = units.map(u => Number(u.rent2)).filter(p => p > 0);
                  
                  
                  if (marketPrices.length > 0) {
                    // First, identify units with deals if deals are available
                    let unitsWithDeals = [];
                    let unitsWithoutDeals = [];
                    
                    if (deals && Array.isArray(deals)) {
                      units.forEach(unit => {
                        const marketPrice = Number(unit.rent1);
                        if (marketPrice <= 0) return; // Skip units with no valid price
                        
                        // Check if this unit has a deal
                        const hasMatchingDeal = deals.some(deal => {
                          // Match by unit code if available (most accurate)
                          if (unit.unit && deal.unit) {
                            return unit.unit === deal.unit;
                          }
                          // Otherwise match by floorplan name in sdesc
                          if (deal.sdesc) {
                            const dealFloorplanName = deal.sdesc.split(' - ').pop().trim();
                            return dealFloorplanName === fp.floorplanName.trim();
                          }
                          return false;
                        });
                        
                        if (hasMatchingDeal) {
                          unitsWithDeals.push(unit);
                        } else {
                          unitsWithoutDeals.push(unit);
                        }
                      });
                    } else {
                      // No deals available, all units go to unitsWithoutDeals
                      unitsWithoutDeals = units.filter(unit => Number(unit.rent1) > 0);
                    }
                    
                    let selectedUnit = null;
                    let lowestMarketPrice = Infinity;
                    let correspondingProformaPrice = null;
                    
                    // Priority 1: Select lowest price unit among units with deals
                    if (unitsWithDeals.length > 0) {
                      unitsWithDeals.forEach(unit => {
                        const marketPrice = Number(unit.rent1);
                        const proformaPrice = Number(unit.rent2);
                        
                        if (marketPrice < lowestMarketPrice) {
                          lowestMarketPrice = marketPrice;
                          correspondingProformaPrice = proformaPrice || marketPrice;
                          selectedUnit = unit;
                        }
                      });
                    }
                    // Priority 2: If no units with deals, select lowest price unit overall
                    else if (unitsWithoutDeals.length > 0) {
                      unitsWithoutDeals.forEach(unit => {
                        const marketPrice = Number(unit.rent1);
                        const proformaPrice = Number(unit.rent2);
                        
                        if (marketPrice < lowestMarketPrice) {
                          lowestMarketPrice = marketPrice;
                          correspondingProformaPrice = proformaPrice || marketPrice;
                          selectedUnit = unit;
                        }
                      });
                    }
                    
                    // Check if the selected unit has a deal
                    let dealText = null;
                    if (selectedUnit && deals && Array.isArray(deals)) {
                      // Find matching deals for this specific unit
                      const matchingDeals = deals.filter(deal => {
                        // Match by unit code if available (most accurate)
                        if (selectedUnit.unit && deal.unit) {
                          return selectedUnit.unit === deal.unit;
                        }
                        // Otherwise match by floorplan name in sdesc
                        if (deal.sdesc) {
                          const dealFloorplanName = deal.sdesc.split(' - ').pop().trim();
                          return dealFloorplanName === fp.floorplanName.trim();
                        }
                        return false;
                      });
                      
                      if (matchingDeals.length > 0) {
                        // Use sdesc1 (special description) like unitDisplayCards does
                        dealText = matchingDeals[0].sdesc1 || matchingDeals[0].ldesc || matchingDeals[0].sdesc || "Special Offer";
                      }
                    }
                    
                    allPrices[fp.id] = {
                      marketPrice: lowestMarketPrice,           // Market price (actual/discounted)
                      proformaPrice: correspondingProformaPrice, // Proforma price (list/original)
                      dealText: dealText,                        // Deal description if available
                      // Keep legacy names for compatibility
                      startPrice: lowestMarketPrice,
                      endPrice: lowestMarketPrice,
                      originalPrice: correspondingProformaPrice
                    };
                  }
                }
              });
          }
        } catch (error) {
          console.error(`Error fetching prices for community ${propId}:`, error);
        }
      }

      setCrossSellPrices(allPrices);
      // Initialize filtered cross-sell with all cross-sell floorplans
      setFilteredCrossSell(crossSellingFloorplans);
    };

    fetchCrossSellPrices();
  }, [crossSellingFloorplans, deals]);

  const restoreViewing = () => {
    //console.log('viewing',viewing);
    const uniqueFloorplanNames = new Set(viewing); // create a Set with the unique values in the current viewing array
    flrplns.forEach((n) => {
      if (
        n.node.communitySlug === slug &&
        !uniqueFloorplanNames.has(n.node.floorplanName)
      ) {
        uniqueFloorplanNames.add(n.node.floorplanName); // add the floorplanName to the Set if it's not already in it
      }
    });
    setViewing(Array.from(uniqueFloorplanNames)); // convert the Set back to an array and update the state
    // Also restore all cross-sell floorplans
    setFilteredCrossSell(crossSellingFloorplans);
  };

  const checkAv = (uname) => {
    // Should return true if vacant exists
    // Returns false if no vacant exists
    let status = displayAv(uname); // Only do it once
    return status != "Contact Us" ? true : false;
  };

  const displayAv = (uname) => {
    if (uname == "The Conroy") {
      // Silly hack
      uname = "Parc";
    }

    const plusPlans = [
      "Quartz+",
      "Emerald+",
      "Jade+",
      "Onyx+",
      "Kipling+",
      "Chatham+",
      "Granada+",
      "Avalon+",
      "Alafia River+",
      "Blue Spring+",
      "Mills+",
      "Central+",
      "Corrine+",
      "Park+",
    ];
    if (uname.endsWith("+") && !plusPlans.includes(uname)) {
      // MORE hacks
      uname = uname.slice(0, -1);
    }
    uname = uname.trim();

    const notav = [
      "Occupied No Notice",
      "Notice Rented",
      "Vacant Rented Ready",
      "Vacant Rented Not Ready",
      "Down",
    ]; // Not available status
    const avnow = ["Vacant Unrented Not Ready", "Vacant Unrented Ready"]; // available now status
    const avsoon = ["Notice Unrented"]; // available soon status

    let status = "Contact Us"; // Default status

    let foundAvailableSoon = false;

    for (const unit of available) {
      const fpname = getFloorplanNameYardi(unit.amenities).trim();

      if (fpname.trim() === uname) {
        const unitStatus = unit.status.trim();

        if (avnow.includes(unitStatus)) {
          return "Available Now"; // Return immediately if Available Now
        } else if (avsoon.includes(unitStatus)) {
          foundAvailableSoon = true; // Mark that we found an Available Soon
        }
      }
    }

    if (foundAvailableSoon) {
      return "Available Soon";
    }

    return status; // Return Contact Us
  };

  const filterBeds = (e) => {
    restoreViewing();
    if (Number(e.target.value) >= 0) {
      let new_viewing = [];
      flrplns.map((n) => {
        if (Number(n.node.beds) == Number(e.target.value)) {
          new_viewing.push(n.node.floorplanName);
        }
      });
      setViewing(new_viewing);
      
      // Also filter cross-sell floorplans
      if (crossSellingFloorplans && crossSellingFloorplans.length > 0) {
        const filtered = crossSellingFloorplans.filter(fp => 
          Number(fp.beds) == Number(e.target.value)
        );
        setFilteredCrossSell(filtered);
      }
    } else {
      // Reset cross-sell filter when "# of bedroom" is selected
      setFilteredCrossSell(crossSellingFloorplans);
    }
  };

  const filterAv = (e) => {
    restoreViewing();
    if (e.target.value == "now") {
      // Filter for Available Now only
      let new_viewing = [];
      flrplns.map((n) => {
        const status = displayAv(n.node.floorplanName);
        if (status === "Available Now") {
          new_viewing.push(n.node.floorplanName);
        }
      });
      setViewing(new_viewing);
      // Keep showing all cross-sell floorplans as alternatives
      setFilteredCrossSell(crossSellingFloorplans);
    } else if (e.target.value == "soon") {
      // Filter for Available Soon only
      let new_viewing = [];
      flrplns.map((n) => {
        const status = displayAv(n.node.floorplanName);
        if (status === "Available Soon") {
          new_viewing.push(n.node.floorplanName);
        }
      });
      setViewing(new_viewing);
      // Keep showing all cross-sell floorplans as alternatives
      setFilteredCrossSell(crossSellingFloorplans);
    } else if (e.target.value == "all") {
      // Show all floorplans and cross-sell floorplans
      restoreViewing();
      setFilteredCrossSell(crossSellingFloorplans);
    }
  };

  const filterBath = (e) => {
    restoreViewing();
    if (Number(e.target.value) > 0) {
      let new_viewing = [];
      flrplns.map((n) => {
        if (Number(n.node.numBaths) >= Number(e.target.value)) {
          new_viewing.push(n.node.floorplanName);
        }
      });
      setViewing(new_viewing);
      
      // Also filter cross-sell floorplans
      if (crossSellingFloorplans && crossSellingFloorplans.length > 0) {
        const filtered = crossSellingFloorplans.filter(fp => {
          const baths = fp.numBaths || fp.baths;
          return Number(baths) >= Number(e.target.value);
        });
        setFilteredCrossSell(filtered);
      }
    } else {
      // Reset cross-sell filter when "# of bathroom" is selected
      setFilteredCrossSell(crossSellingFloorplans);
    }
  };

  const filterPrice = (e) => {
    restoreViewing();
    if (Number(e.target.value) > 0) {
      let new_viewing = [];
      flrplns.map((n) => {
        if (
          Number(reBaseRent(n.node.floorplanName)) <= Number(e.target.value)
        ) {
          new_viewing.push(n.node.floorplanName);
        }
      });
      setViewing(new_viewing);
      
      // Also filter cross-sell floorplans
      if (crossSellingFloorplans && crossSellingFloorplans.length > 0) {
        const filtered = crossSellingFloorplans.filter(fp => {
          const prices = crossSellPrices[fp.id];
          const price = prices ? (prices.marketPrice || prices.startPrice || 0) : 0;
          return price > 0 && price <= Number(e.target.value);
        });
        setFilteredCrossSell(filtered);
      }
    } else {
      // Reset cross-sell filter when "Max Rent" is selected
      setFilteredCrossSell(crossSellingFloorplans);
    }
  };

  const reBaseRent = (fpname) => {
    if (!allPrices || Object.keys(allPrices).length === 0) {
      // If no prices loaded, use startingPrices as fallback
      if (startingPrices && startingPrices[fpname]) {
        return parseInt(startingPrices[fpname]);
      }
      return 99999; // Return high number to not filter out when no price found
    }
    
    // Try exact match first
    if (allPrices[fpname]) {
      return parseInt(allPrices[fpname]);
    }
    
    // Try case-insensitive search
    const keys = Object.keys(allPrices);
    const searchName = fpname.toLowerCase().replace("the ", "");
    const foundKey = keys.find((key) => key.toLowerCase().replace("the ", "") === searchName);

    if (foundKey) {
      return parseInt(allPrices[foundKey]);
    }
    
    // If still not found, try startingPrices as fallback
    if (startingPrices && startingPrices[fpname]) {
      return parseInt(startingPrices[fpname]);
    }
    
    return 99999; // Return high number to not filter out when no price found
  };

  return (
    <section>
      <div className=" container">
        <div className="columns is-padded">
          <div className="column">
            <h2
              style={{
                textAlign: "center",
                fontFamily: "MoonTime",
                fontSize: "5rem",
              }}
              className={fontColorBlue}
            >
              Floorplans
            </h2>
            <hr />
            <div className="columns" id="filters">
              <div className="column">&nbsp;</div>
              <div className="column">
                <div className="columns">
                  <div className="column">
                    <div className="select">
                      <select onChange={filterBeds}>
                        <option># of bedroom</option>
                        <option value="0">Studio</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                      </select>
                    </div>
                  </div>
                  <div className="column">
                    <div className="select">
                      <select onChange={filterBath}>
                        <option># of bathroom</option>
                        <option value="1">1</option>
                        <option value="1.5">1.5</option>
                        <option value="2">2</option>
                        <option value="2.5">2.5</option>
                        <option value="3">3</option>
                      </select>
                    </div>
                  </div>
                  <div className="column">
                    <div className="select">
                      <select onChange={filterPrice}>
                        <option>Max Rent</option>
                        <option value="1000">$1,000</option>
                        <option value="1500">$1,500</option>
                        <option value="2000">$2,000</option>
                        <option value="2500">$2,500</option>
                        <option value="3000">$3,000</option>
                        <option value="3500">$3,500</option>
                        <option value="4000">$4,000</option>
                      </select>
                    </div>
                  </div>
                  <div className="column">
                    <div className="select">
                      <select onChange={filterAv}>
                        <option>Availability</option>
                        <option value="now">Available Now</option>
                        <option value="soon">Available Soon</option>
                        <option value="all">Show All</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="column">&nbsp;</div>
            </div>
          </div>
        </div>
      </div>
      <br />
      <br />
      <div className=" container">
        <div className="columns is-padded">
          <div
            className="column"
            style={{ maxWidth: "1344px", overflow: "scroll" }}
          >
            <p
              style={{ maxWidth: "1344px", textAlign: "center" }}
              className={fontColorDark}
            >
              All prices, availability and limited-time offers are subject to
              change without notice. To ensure you get the {cname} apartment that you
              want, apply online and reserve your apartment home today!
              <br />
              <br />
              Don't see what you want available? Contact us to join our
              waitlist.
            </p>
            {floorplanCalloutField && (
              <p className={`has-text-centered ${calloutField2}`} style={{ fontSize: '1.5rem', marginTop: '2rem' }}>
                {floorplanCalloutField}
              </p>
            )}
            <br />
            <br />
            {loadingFloorPlans ? (
              <p>Loading...</p>
            ) : viewing.length > 0 && available.length > 0 ? (
              <FloorplansDisplay
                floorPlans={floorPlans}
                viewing={viewing}
                startingPrices={startingPrices}
                endingPrices={endingPrices}
                startingPricesAv={startingPricesAv}
                endingPricesAv={endingPricesAv}
                displayAv={displayAv}
                slug={slug}
                area={area}
                utilitiesIncluded={utilitiesIncluded}
                citySlug={citySlug}
                floorplanDeals={floorplanDeals}
                communityName={cname}
                email={email}
                soldOutBedCategories={soldOutBedCategories}
                crossSellingFloorplans={filteredCrossSell}
                available={available}
                crossSellPrices={crossSellPrices}
              />
            ) : (
              <p className="has-text-centered">
                No Floorplans Found
                <br />
                <br />
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Floorplans;
